import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useCursor } from "@/context/CursorContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

const ShowcaseCard = ({ showcase, index }) => {
  const { setCursor, reset } = useCursor();
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: 0.05 * index, ease: EASE }}
      className="group"
    >
      <Link
        to={`/work/${showcase.slug}`}
        onMouseEnter={() => setCursor("link")}
        onMouseLeave={reset}
        data-testid={`showcase-card-${showcase.slug}`}
        className="block"
      >
        <div className="relative aspect-[4/3] overflow-hidden bg-surface">
          {showcase.thumbnail ? (
            <img
              src={showcase.thumbnail}
              alt={showcase.name}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover grayscale transition-all duration-700 group-hover:grayscale-0 group-hover:scale-105"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-void">
              <span className="font-display text-6xl font-bold text-bone/10 uppercase">
                {(showcase.name || "").slice(0, 2)}
              </span>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-void/85 via-void/30 to-transparent" />
          <span className="absolute right-5 top-5 rounded-full bg-void/60 px-2.5 py-1 font-mono text-[10px] tracking-[0.25em] uppercase text-bone backdrop-blur-sm">
            {showcase.video_count} {showcase.video_count === 1 ? "piece" : "pieces"}
          </span>
          <div className="absolute inset-x-5 bottom-5 flex items-end justify-between gap-4">
            <h3 className="font-work text-3xl md:text-5xl font-medium uppercase text-bone group-hover:text-navy-ink transition-colors duration-300 break-words">
              {showcase.name}
            </h3>
            <span className="shrink-0 font-display text-4xl text-bone/40 group-hover:text-navy-ink group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-300">
              ↗
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

/**
 * Grid of client / showcase cards. Used on the Home Work section AND on the
 * standalone /work index page. When you click a card you go to that
 * client's dedicated page — /work/:slug.
 */
export const ShowcaseGrid = React.forwardRef(({ initial }, ref) => {
  const [showcases, setShowcases] = useState(initial || []);
  const [loading, setLoading] = useState(!initial);

  useEffect(() => {
    if (initial) return;
    axios
      .get(`${API}/showcases`)
      .then((res) => setShowcases(res.data))
      .catch((e) => console.error("showcases fetch failed", e))
      .finally(() => setLoading(false));
  }, [initial]);

  return (
    <div ref={ref} data-testid="showcases-grid">
      {loading && (
        <p className="mt-16 font-mono text-sm text-bone/40 tracking-widest uppercase text-center">
          Loading…
        </p>
      )}
      {!loading && showcases.length === 0 && (
        <p className="mt-16 font-mono text-sm text-bone/40 tracking-widest uppercase text-center">
          No showcases yet.
        </p>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
        {showcases.map((s, i) => (
          <ShowcaseCard key={s.id} showcase={s} index={i} />
        ))}
      </div>
    </div>
  );
});

ShowcaseGrid.displayName = "ShowcaseGrid";
